/**
 * Outreach cockpit queries and mutations.
 *
 * Reads go straight to Supabase under the admin/owner-only `crm_outreach_select`
 * RLS policy. Writes go through the service-role endpoints: the table grants
 * `authenticated` nothing but SELECT (20260731120000_crm_outreach_claim_and_finalize.sql
 * revokes the earlier narrow UPDATE grant), so every state change — claim,
 * sent, skipped, release — is a server-side action, never a client-side write.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import {
  crmKeys,
  resolvedScopeKey,
  useCrmQueryScope,
  type CrmQueryScope,
} from '@/hooks/crm/useContacts';
import {
  currentMonthRange,
  OUTREACH_STATES,
  type OutreachBrand,
  type OutreachRow,
  type OutreachState,
} from '@/lib/crm/outreach';

export const OUTREACH_PAGE_SIZE = 25;

export const outreachKeys = {
  all: ['outreach'] as const,
  list: (scope: CrmQueryScope, brand: OutreachBrand, state: OutreachState, page: number) =>
    [...outreachKeys.all, 'list', ...scope, brand, state, page] as const,
  counts: (scope: CrmQueryScope, brand: OutreachBrand) =>
    [...outreachKeys.all, 'counts', ...scope, brand] as const,
  notesSpent: (scope: CrmQueryScope) =>
    [...outreachKeys.all, 'notesSpent', ...scope] as const,
};

// Mirrors `src/hooks/crm/useLeadInbox.ts` and `src/hooks/use-acquisition-signals.ts`
// on purpose — a second auth idiom is how the LinkedIn analytics 401 happened.
const accessToken = async (): Promise<string> => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Not authenticated');
  return session.access_token;
};

const apiError = async (response: Response): Promise<Error> => {
  const body = await response.json().catch(() => ({})) as {
    error?: { message?: string; code?: string };
    message?: string;
  };
  const error = new Error(body.error?.message ?? body.message ?? `API error: ${response.status}`);
  error.name = body.error?.code ?? 'ApiError';
  return error;
};

// ═══════════════════════════════════════════════════════════════════════════
// QUERIES
// ═══════════════════════════════════════════════════════════════════════════

export interface OutreachPage {
  rows: OutreachRow[];
  totalCount: number;
}

export function useOutreachQueue(brand: OutreachBrand, state: OutreachState, page: number) {
  const scope = useCrmQueryScope();

  return useQuery<OutreachPage>({
    queryKey: outreachKeys.list(resolvedScopeKey(scope), brand, state, page),
    queryFn: async () => {
      const from = page * OUTREACH_PAGE_SIZE;
      const to = from + OUTREACH_PAGE_SIZE - 1;

      const base = supabase
        .from('crm_outreach')
        .select('*', { count: 'exact' })
        .eq('organization_id', scope![1])
        .eq('brand', brand)
        .eq('state', state);

      // For a connection request sitting unanswered, acceptance is not
      // observable from any API we hold — connection_sent_at, oldest first,
      // is the only proxy for "this one is not coming" (see
      // idx_crm_outreach_awaiting). Every other rung keeps newest-first.
      const ordered = state === 'connection_sent'
        ? base
          .order('connection_sent_at', { ascending: true, nullsFirst: false })
          .order('id', { ascending: true })
        : base
          .order('created_at', { ascending: false })
          .order('id', { ascending: false });

      const { data, error, count } = await ordered.range(from, to);

      if (error) throw error;
      return { rows: (data ?? []) as OutreachRow[], totalCount: count ?? 0 };
    },
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

export type OutreachCounts = Record<OutreachState, number>;

export function useOutreachCounts(brand: OutreachBrand) {
  const scope = useCrmQueryScope();

  return useQuery<OutreachCounts>({
    queryKey: outreachKeys.counts(resolvedScopeKey(scope), brand),
    queryFn: async () => {
      const entries = await Promise.all(
        OUTREACH_STATES.map(async (state) => {
          const { count, error } = await supabase
            .from('crm_outreach')
            .select('id', { count: 'exact', head: true })
            .eq('organization_id', scope![1])
            .eq('brand', brand)
            .eq('state', state);

          if (error) throw error;
          return [state, count ?? 0] as const;
        }),
      );

      return Object.fromEntries(entries) as OutreachCounts;
    },
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

/**
 * Personalised-note requests spent this calendar month, across every brand.
 * Deliberately not brand-scoped: the LinkedIn ration lives on the operator's
 * own account, not on any one brand persona — matches
 * `idx_crm_outreach_note_spend` in
 * `20260731150000_crm_outreach_note_budget.sql`, which indexes
 * `(organization_id, connection_sent_at)` with no brand column. No ceiling is
 * enforced or even known here; this is a fact the operator holds the limit
 * against, not a gate.
 */
export function useOutreachNotesSpent() {
  const scope = useCrmQueryScope();

  return useQuery<number>({
    queryKey: outreachKeys.notesSpent(resolvedScopeKey(scope)),
    queryFn: async () => {
      const { start, end } = currentMonthRange(new Date());

      const { count, error } = await supabase
        .from('crm_outreach')
        .select('id', { count: 'exact', head: true })
        .eq('organization_id', scope![1])
        .eq('connection_note_used', true)
        .gte('connection_sent_at', start)
        .lt('connection_sent_at', end);

      if (error) throw error;
      return count ?? 0;
    },
    enabled: scope !== null,
    staleTime: 30_000,
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// MUTATIONS
// ═══════════════════════════════════════════════════════════════════════════

export interface ClaimOutreachInput {
  id: string;
}

export interface ClaimOutreachResponse {
  success: true;
  state: OutreachState;
  connectionNote: string | null;
  message: string;
  followup: string | null;
  linkedinUrl: string;
  claimedUntil: string;
}

export function useClaimOutreach() {
  const queryClient = useQueryClient();

  return useMutation<ClaimOutreachResponse, Error, ClaimOutreachInput>({
    mutationFn: async (input) => {
      const token = await accessToken();
      const response = await fetch('/api/crm/outreach-claim', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw await apiError(response);
      return response.json() as Promise<ClaimOutreachResponse>;
    },
    // A 409 means someone else holds the claim: retrying automatically would
    // just contend for the same lease again. The caller decides what to do
    // next (toast + refetch), never a silent retry.
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachKeys.all });
      await queryClient.invalidateQueries({ queryKey: crmKeys.all });
    },
  });
}

export interface FinalizeOutreachInput {
  id: string;
  decision: 'connection_sent' | 'connected' | 'declined' | 'sent' | 'skipped' | 'release';
  name?: string;
  company?: string;
  sentOn?: string;
  dueDate?: string;
  note?: string;
  skippedReason?: string;
  /** Only meaningful on the `connection_sent` decision — see outreach-finalize.ts. */
  noteUsed?: boolean;
}

export interface FinalizeOutreachResponse {
  success: true;
  outreach: {
    id: string;
    state: OutreachState;
    contactId: string | null;
    sentAt: string | null;
  };
}

export function useFinalizeOutreach() {
  const queryClient = useQueryClient();

  return useMutation<FinalizeOutreachResponse, Error, FinalizeOutreachInput>({
    mutationFn: async (input) => {
      const token = await accessToken();
      const response = await fetch('/api/crm/outreach-finalize', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
      if (!response.ok) throw await apiError(response);
      return response.json() as Promise<FinalizeOutreachResponse>;
    },
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: outreachKeys.all });
      // Sent/skipped/release can touch a contact's stage and due date, so the
      // rest of the cockpit's counts are now stale too.
      await queryClient.invalidateQueries({ queryKey: crmKeys.all });
    },
  });
}
